import { readJson, writeJson } from './storage';

// Offline-first storage for Conversion reports (Reporte Diario).
// Stored as raw "production" records with structured fields:
// conversion_header, rows, downtime.

const RECORDS_KEY = 'conversion_report_records_v1';
const QUEUE_KEY = 'conversion_report_queue_v1';

export const makeConversionReportKey = ({ site_id, date, line_id, shift_id }) => {
  const s = site_id ?? 'null';
  return `${s}:${date}:${line_id}:${shift_id}:conversion`;
};

export const loadConversionReportRecords = () => readJson(RECORDS_KEY, {});
export const saveConversionReportRecords = (records) => writeJson(RECORDS_KEY, records);

export const loadConversionReportQueue = () => readJson(QUEUE_KEY, []);
export const saveConversionReportQueue = (queue) => writeJson(QUEUE_KEY, queue);

export const upsertLocalConversionReport = (record, localStatus = 'synced', extra = {}) => {
  const records = loadConversionReportRecords();
  const key = makeConversionReportKey(record);
  const prev = records[key] || {};

  records[key] = {
    ...prev,
    ...record,
    _local: {
      ...(prev._local || {}),
      status: localStatus,
      updatedAt: Date.now(),
      ...extra,
    },
  };

  saveConversionReportRecords(records);
  return { key, record: records[key] };
};

export const enqueueConversionReportSync = (key) => {
  const queue = loadConversionReportQueue();
  if (!queue.includes(key)) {
    queue.push(key);
    saveConversionReportQueue(queue);
  }
  return queue;
};

export const dequeueConversionReportSync = (key) => {
  const queue = loadConversionReportQueue().filter((k) => k !== key);
  saveConversionReportQueue(queue);
  return queue;
};

export const removeLocalConversionReport = (key) => {
  const records = loadConversionReportRecords();
  if (records[key]) {
    delete records[key];
    saveConversionReportRecords(records);
  }
  dequeueConversionReportSync(key);
};

export const getPendingConversionReportCount = () => {
  const records = loadConversionReportRecords();
  const queue = loadConversionReportQueue();
  return queue.filter((k) => records[k]?._local?.status === 'pending').length;
};

/**
 * Remove synced (non-pending) local records older than `maxAgeMs`.
 * Pending records are always kept so they can be synced later.
 */
export const purgeStaleLocalRecords = (maxAgeMs = 24 * 60 * 60 * 1000) => {
  const records = loadConversionReportRecords();
  const queue = new Set(loadConversionReportQueue());
  const now = Date.now();
  let changed = false;

  for (const [key, rec] of Object.entries(records)) {
    // Never purge records that are pending sync
    if (queue.has(key)) continue;
    if (rec?._local?.status === 'pending') continue;

    const updatedAt = rec?._local?.updatedAt || 0;
    if (now - updatedAt > maxAgeMs) {
      delete records[key];
      changed = true;
    }
  }

  if (changed) saveConversionReportRecords(records);
  return changed;
};

export const listLocalConversionReports = (filter = {}) => {
  const { site_id, date, line_id, shift_id } = filter;
  const records = loadConversionReportRecords();
  const items = Object.values(records);

  return items
    .filter((r) => {
      if (site_id != null && r.site_id != null && String(r.site_id) !== String(site_id)) return false;
      if (date && r.date !== date) return false;
      if (line_id && String(r.line_id) !== String(line_id)) return false;
      if (shift_id && String(r.shift_id) !== String(shift_id)) return false;
      return true;
    })
    .sort((a, b) => {
      if (a?.updated_at && b?.updated_at) return new Date(b.updated_at) - new Date(a.updated_at);
      if (a?.id != null && b?.id != null) return b.id - a.id;
      if (a?.date && b?.date) return b.date.localeCompare(a.date);
      return 0;
    });
};

